import axios from 'axios'
import { resolveLocaleByRegion, REGION_UNKNOWN, normalizeRegionCode } from '@/utils/region-context'

const GEOJS_GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/geo.json'
const GEOIP_CACHE_KEY = 'kkkorGeoIpContext'
const GEOIP_CACHE_TTL = 5 * 60 * 1000
const GEOIP_REQUEST_TIMEOUT = 3000

function canUseSessionStorage() {
  return typeof window !== 'undefined' && !!window.sessionStorage
}

function createDefaultGeoIpContext() {
  return {
    region: REGION_UNKNOWN,
    locale: resolveLocaleByRegion(REGION_UNKNOWN),
    ip: '',
    resolved: false
  }
}

function readGeoIpCache() {
  if (!canUseSessionStorage()) {
    return null
  }

  try {
    const rawCache = window.sessionStorage.getItem(GEOIP_CACHE_KEY)
    if (!rawCache) {
      return null
    }
    const parsedCache = JSON.parse(rawCache)
    const cachedAt = Number(parsedCache.cachedAt) || 0
    if (!cachedAt || Date.now() - cachedAt > GEOIP_CACHE_TTL) {
      window.sessionStorage.removeItem(GEOIP_CACHE_KEY)
      return null
    }
    const cachedData = parsedCache.data || {}
    return normalizeGeoIpPayload(cachedData, cachedData.resolved === true)
  } catch (error) {
    window.sessionStorage.removeItem(GEOIP_CACHE_KEY)
    return null
  }
}

function writeGeoIpCache(context) {
  if (!canUseSessionStorage()) {
    return
  }

  try {
    window.sessionStorage.setItem(GEOIP_CACHE_KEY, JSON.stringify({
      cachedAt: Date.now(),
      data: context
    }))
  } catch (error) {
    window.sessionStorage.removeItem(GEOIP_CACHE_KEY)
  }
}

function normalizeGeoIpPayload(payload = {}, resolved = true) {
  const region = normalizeRegionCode(payload.country_code || payload.region)
  const ip = typeof payload.ip === 'string' ? payload.ip.trim() : ''

  return {
    region,
    locale: resolveLocaleByRegion(region),
    ip,
    resolved
  }
}

export async function resolveGeoIpContext(forceRefresh = false) {
  if (!forceRefresh) {
    const cachedContext = readGeoIpCache()
    if (cachedContext) {
      return cachedContext
    }
  }

  try {
    const response = await axios.get(GEOJS_GEO_ENDPOINT, {
      timeout: GEOIP_REQUEST_TIMEOUT
    })
    const resolvedContext = normalizeGeoIpPayload(response && response.data ? response.data : {})
    writeGeoIpCache(resolvedContext)
    return resolvedContext
  } catch (error) {
    return createDefaultGeoIpContext()
  }
}
